import { useEffect, useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';
import useTable, { getComparator } from 'src/hooks/useTable';
import { validaLabelMenuDatas } from 'src/utils/functions';
import v2_actionListaAvaliacoes from 'src/actions/v2/dashboard/relatorios/v2_actionListaAvaliacoes';
import AvaliacoesRow from 'src/sections/@dashboard/relatorios/AvaliacoesRow';
import getActionListaUsuariosByClienteId from 'src/actions/usuarios/getActionListaUsuariosByClienteId';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import { SelectItem } from 'src/@/components/ui/select';
import { SelectZui } from 'src/components/zui';
import MenuDatas2 from 'src/components/MenuDatas2';
import GenericTable from 'src/components/table/GenericTable';
import { Card, CardContent } from 'src/@/components/ui/card';
import { Button } from 'src/@/components/ui/button';
import { RELATORIOS_DESIGN as d } from 'src/sections/@dashboard/relatorios/Relatorios.design';

const TABLE_HEAD = [
    { id: 'nome', label: 'Cliente', className: 'flex-[2] min-w-[180px]' },
    { id: 'telefone', label: 'Telefone', className: 'flex-1 min-w-[130px]' },
    { id: 'data', label: 'Data Avaliacao', className: 'flex-1 min-w-[150px]' },
    { id: 'atendente', label: 'Atendente', className: 'flex-1 min-w-[150px]' },
    { id: 'nota', label: 'Nota', className: 'w-[100px] text-center' },
];

const ROWS_PER_PAGE = 10;

export default function RelatorioListaAvaliacoes() {
    const [listaUsuarios, setListaUsuarios] = useState([]);
    const [listaCanais, setListaCanais] = useState([]);
    const { user } = useAuth();
    const [inicial, setInicial] = useState(new Date());
    const [final, setFinal] = useState(new Date());
    const [tableData, setTableData] = useState([]);
    const [periodo, setPeriodo] = useState('mes_atual');
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenuDatas = Boolean(anchorEl);
    const [filterUsuario, setFilterUsuario] = useState('');
    const [filterCanal, setFilterCanal] = useState('todos');
    const [page, setPage] = useState(0);

    const {
        order,
        orderBy,
        onSort,
    } = useTable({ defaultOrderBy: 'date_created' });

    const buscarUsuarios = useCallback(async () => {
        try {
            const res = await getActionListaUsuariosByClienteId(user.clienteId);
            setListaUsuarios(res.data.usuarios);
        } catch (error) {
            console.log(error);
        }
    }, [user.clienteId]);

    const buscarCanais = useCallback(async () => {
        try {
            const res = await actionListaCanaisPorCliente(user.clienteId);
            const canais = res.data.canais || res.data || [];
            setListaCanais(Array.isArray(canais) ? canais : []);
        } catch (error) {
            console.log(error);
        }
    }, [user.clienteId]);

    const buscarListaAvaliacoes = useCallback(
        async (value, canalId) => {
            try {
                let inicio2 = moment(inicial).format('YYYY-MM-DD');
                let final2 = moment(final).format('YYYY-MM-DD');
                let querys = `?periodo=${value}&cliente_id=${user.clienteId}&inicio=${inicio2}&final=${final2}&usuario_id=${user.id}`;
                if (canalId && canalId !== 'todos') querys += `&canal_id=${canalId}`;
                const res = await v2_actionListaAvaliacoes(querys);
                setTableData(res.data.data);
                setPage(0);
            } catch (error) {
                console.log(error);
            }
        },
        [inicial, final, user.clienteId, user.id]
    );

    useEffect(() => {
        buscarUsuarios();
        buscarCanais();
        buscarListaAvaliacoes(periodo, filterCanal);
    }, []);

    const onFiltrar = useCallback(
        async (value) => {
            setPeriodo(value);
            buscarListaAvaliacoes(value, filterCanal);
        },
        [buscarListaAvaliacoes, filterCanal]
    );

    const onPesquisar = useCallback(() => {
        buscarListaAvaliacoes('null', filterCanal);
        setPeriodo('null');
    }, [buscarListaAvaliacoes, filterCanal]);

    const handleCanalChange = useCallback((value) => {
        setFilterCanal(value);
        setPage(0);
        buscarListaAvaliacoes(periodo, value);
    }, [buscarListaAvaliacoes, periodo]);

    const dataFiltered = useMemo(
        () => applySortFilter({ tableData, comparator: getComparator(order, orderBy), filterUsuario }),
        [tableData, order, orderBy, filterUsuario]
    );

    const totalPages = Math.ceil(dataFiltered.length / ROWS_PER_PAGE);
    const paginatedData = dataFiltered.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

    const stats = useMemo(() => {
        if (!tableData.length) return null;
        const total = tableData.length;
        const notas = tableData.map((r) => Number(r.nota)).filter((n) => !isNaN(n));
        const media = notas.length ? (notas.reduce((a, b) => a + b, 0) / notas.length).toFixed(1) : 0;
        const positivas = notas.filter((n) => n >= 4).length;
        const negativas = notas.filter((n) => n <= 2).length;
        return { total, media, positivas, negativas };
    }, [tableData]);

    const SIDEBAR_STATS = [
        { label: 'Total', value: stats?.total ?? 0, icon: 'eva:file-text-outline', color: 'violet' },
        { label: 'Media', value: stats?.media ?? 0, icon: 'eva:bar-chart-outline', color: 'blue' },
        { label: 'Positivas', value: stats?.positivas ?? 0, icon: 'eva:trending-up-outline', color: 'green' },
        { label: 'Negativas', value: stats?.negativas ?? 0, icon: 'eva:trending-down-outline', color: 'red' },
    ];

    return (
        <div className={d.page.container}>
            {/* Header */}
            <div className={d.page.header.container}>
                <div className={d.page.header.left}>
                    <div className={d.page.header.iconBox}>
                        <Iconify icon="eva:star-outline" width={28} className={d.page.header.icon} />
                    </div>
                    <div>
                        <h1 className={d.page.header.title}>Relatorio de Avaliacoes</h1>
                        <p className={d.page.header.subtitle}>Visualize e filtre as avaliacoes recebidas</p>
                    </div>
                </div>
            </div>

            {/* Two-column layout */}
            <div className={d.twoColumn.container}>
                {/* Sidebar - Stats */}
                {stats && (
                    <div className={d.twoColumn.sidebar}>
                        {SIDEBAR_STATS.map((stat) => (
                            <Card key={stat.label} className={d.sidebarStat.card}>
                                <CardContent className={d.sidebarStat.cardContent}>
                                    <div className={d.sidebarStat.row}>
                                        <div className={`${d.sidebarStat.iconBox.base} ${d.sidebarStat.iconBox[stat.color]}`}>
                                            <Iconify icon={stat.icon} className={d.sidebarStat.icon[stat.color]} />
                                        </div>
                                        <div className="flex-1">
                                            <p className={d.sidebarStat.label}>{stat.label}</p>
                                            <p className={d.sidebarStat.value}>{stat.value}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Content - Table */}
                <div className={d.twoColumn.content}>
                    <Card className={d.tableCard.container}>
                        <CardContent className={d.tableCard.cardContent}>
                            {/* Filter bar */}
                            <div className={d.tableCard.filterBar}>
                                <h3 className={d.tableCard.filterTitle}>Avaliacoes</h3>
                                <div className={d.tableCard.filterActions}>
                                    <div className={d.filters.group}>
                                        <MenuDatas2
                                            onClose={() => setAnchorEl(null)}
                                            anchorEl={anchorEl}
                                            open={openMenuDatas}
                                            inicial={inicial}
                                            final={final}
                                            changeInicial={(newValue) => setInicial(newValue)}
                                            changeFinal={(newValue) => setFinal(newValue)}
                                            content={validaLabelMenuDatas(periodo, inicial, final)}
                                            onSelect={(value) => {
                                                setPeriodo(value);
                                                setAnchorEl(null);
                                                onFiltrar(value);
                                            }}
                                            onPesquisar={() => onPesquisar()}
                                        />
                                    </div>
                                    <SelectZui
                                        value={filterCanal}
                                        list={listaCanais}
                                        onChange={handleCanalChange}
                                        emptyOption={{ value: 'todos', label: 'Todos os canais' }}
                                        itemSelect={(item) => (
                                            <SelectItem key={item._id} value={item._id}>
                                                {item.nome}
                                            </SelectItem>
                                        )}
                                    />
                                    <SelectZui
                                        value={filterUsuario}
                                        list={listaUsuarios}
                                        onChange={(e) => { setFilterUsuario(e); setPage(0); }}
                                        emptyOption={{ value: null, label: 'Todos os usuarios' }}
                                        itemSelect={(item) => (
                                            <SelectItem key={item._id} value={item.nomeFantasia || 'undefined'}>
                                                {item.firstName}
                                            </SelectItem>
                                        )}
                                    />
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 text-xs"
                                        onClick={() => buscarListaAvaliacoes(periodo, filterCanal)}
                                    >
                                        <Iconify icon="mdi:refresh" className="mr-1 w-3.5 h-3.5" />
                                        Atualizar
                                    </Button>
                                </div>
                            </div>

                            {/* Table content */}
                            {dataFiltered.length > 0 ? (
                                <div className="overflow-auto flex-1 min-h-0">
                                    <GenericTable headLabels={TABLE_HEAD}>
                                        {paginatedData.map((row, index) => (
                                            <AvaliacoesRow key={row._id || index} row={row} />
                                        ))}
                                    </GenericTable>
                                </div>
                            ) : (
                                <div className={d.emptyState.container}>
                                    <div className={d.emptyState.iconBox}>
                                        <Iconify icon="eva:star-outline" className={d.emptyState.icon} />
                                    </div>
                                    <h3 className={d.emptyState.title}>Nenhuma avaliacao encontrada</h3>
                                    <p className={d.emptyState.description}>Nao foram encontradas avaliacoes para o periodo selecionado.</p>
                                </div>
                            )}

                            {/* Pagination */}
                            {dataFiltered.length > ROWS_PER_PAGE && (
                                <div className={d.pagination.container}>
                                    <p className={d.pagination.text}>
                                        Mostrando {paginatedData.length} de {dataFiltered.length} avaliacoes
                                    </p>
                                    <div className={d.pagination.buttons}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                            disabled={page <= 0}
                                            onClick={() => setPage((p) => p - 1)}
                                        >
                                            <Iconify icon="mdi:chevron-left" className="w-4 h-4" />
                                        </Button>
                                        <span className={d.pagination.pageInfo}>{page + 1} / {totalPages}</span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                            disabled={page >= totalPages - 1}
                                            onClick={() => setPage((p) => p + 1)}
                                        >
                                            <Iconify icon="mdi:chevron-right" className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}

function applySortFilter({ tableData, comparator, filterUsuario }) {
    const stabilizedThis = tableData.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    tableData = stabilizedThis.map((el) => el[0]);
    if (filterUsuario) {
        tableData = tableData.filter((item) => item.atendente.toLowerCase().indexOf(filterUsuario.toLowerCase()) !== -1);
    }
    return tableData;
}
